// Типы документов в комплекте версии договора и их маршрутизация.
//
// Зеркалит доменный enum `DocumentKind` (backend: osnova-core/src/domain/legal.rs,
// миграция 0208). Тип решает, попадёт ли файл в промпт юр-агента: комплект на
// стройке — сотни файлов, и сметы с чертежами раньше уезжали в анализ наравне
// с договором, раздувая его в разы. Юрист должен видеть это решение в карточке
// и уметь его поправить — отсюда лейблы и тон бейджа.

export type DocumentKind =
  | 'contract'
  | 'protocol_of_disagreement'
  | 'requisites'
  | 'specification'
  | 'schedule'
  | 'estimate'
  | 'ks_act'
  | 'receipt'
  | 'drawing'
  | 'photo'
  | 'other';

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  contract: 'Договор',
  protocol_of_disagreement: 'Протокол разногласий',
  requisites: 'Реквизиты',
  specification: 'Спецификация',
  schedule: 'График',
  estimate: 'Смета',
  ks_act: 'КС-2 / КС-3',
  receipt: 'Платёжный документ',
  drawing: 'Чертёж',
  photo: 'Фото',
  other: 'Не определён',
};

/** Порядок в выпадающем списке: сперва то, что идёт в анализ. */
export const DOCUMENT_KIND_ORDER: DocumentKind[] = [
  'contract',
  'protocol_of_disagreement',
  'requisites',
  'specification',
  'schedule',
  'estimate',
  'ks_act',
  'receipt',
  'drawing',
  'photo',
  'other',
];

/** Типы, которые уходят в промпт юр-агента. Должно совпадать с `DocumentKind::LEGAL`. */
export const LEGAL_KINDS: readonly DocumentKind[] = [
  'contract',
  'protocol_of_disagreement',
  'requisites',
];

/** Серая зона: в анализ сразу не идёт, но может быть дозапрошена агентом. */
export const GREY_ZONE_KINDS: readonly DocumentKind[] = ['specification', 'schedule'];

export function isLegalKind(kind: string | null | undefined): boolean {
  return !!kind && (LEGAL_KINDS as readonly string[]).includes(kind);
}

export function isGreyZoneKind(kind: string | null | undefined): boolean {
  return !!kind && (GREY_ZONE_KINDS as readonly string[]).includes(kind);
}

export function documentKindLabel(kind: string | null | undefined): string {
  if (!kind) return 'Не определён';
  return DOCUMENT_KIND_LABELS[kind as DocumentKind] ?? kind;
}

/** Тон бейджа: зелёный — идёт в анализ, жёлтый — серая зона, серый — не идёт. */
export function documentKindTone(
  kind: string | null | undefined,
): 'success' | 'warning' | 'neutral' {
  if (isLegalKind(kind)) return 'success';
  if (isGreyZoneKind(kind)) return 'warning';
  return 'neutral';
}

/** Откуда взялся тип — объяснение для подсказки юристу. */
export function kindSourceHint(source: string | null | undefined): string {
  switch (source) {
    case 'lawyer':
      return 'Тип указан юристом вручную';
    case 'llm':
      return 'Тип определён ИИ по содержимому документа';
    case 'heuristic':
      return 'Тип определён по имени файла';
    case 'pending':
      return 'Тип ещё не определён — идёт обработка комплекта';
    default:
      return 'Тип не определён';
  }
}

/**
 * Короткая подпись источника — под бейджем типа в таблице. Отвечает на вопрос
 * юриста «а это я выбрал или система?»: `heuristic`/`llm` — определено
 * автоматически, `lawyer` — правка руками. Полная формулировка — в
 * `kindSourceHint` (tooltip).
 */
export function kindSourceCaption(source: string | null | undefined): string {
  switch (source) {
    case 'lawyer':
      return 'вручную';
    case 'llm':
      return 'определил ИИ по тексту';
    case 'heuristic':
      return 'определено по имени файла';
    case 'pending':
      return 'определяется…';
    default:
      return '';
  }
}

/** Тип ещё определяется системой (классификация комплекта в процессе). */
export function isKindPending(source: string | null | undefined): boolean {
  return (source ?? 'pending') === 'pending';
}

/** Человеческое объяснение, что этот тип означает для анализа. */
export function documentKindAnalysisHint(kind: string | null | undefined): string {
  if (isLegalKind(kind)) return 'Идёт в юридический анализ';
  if (isGreyZoneKind(kind)) return 'В анализ не подаётся, но агент может запросить';
  return 'В юридический анализ не идёт';
}
